// =====================================================================
// tpCustomer — DataProvider
//
// Fetches all nine resources from the backend API in parallel on first
// mount, populates window.TPCRM_DATA (keeping the same shape as the
// original data.jsx), then renders children.
//
// Also exposes window.tpReload(key) — pages call this after a create /
// update / delete to re-fetch a single resource and trigger a React
// re-render. The api.js wrappers call it automatically.
//
// Static lookup tables (MEDDPICCR_KEYS, STAGES, FORECAST_CATEGORIES)
// are defined here as constants — they are schema definitions, not DB
// rows, so no API endpoint is needed for them.
// =====================================================================

// ── Static lookup constants (unchanged from original data.jsx) ────────

// MEDDPICCR — the single qualification framework used across the app.
// The R element is Risk; it aligns the opportunity checklist with the
// MEDDPICCR persona process pages under /meddpicc/.
const MEDDPICCR_KEYS = [
  { key: "metrics",          label: "Metrics",           short: "M", desc: "Quantified business impact documented." },
  { key: "economicBuyer",    label: "Economic Buyer",    short: "E", desc: "Identified, named, and directly accessed." },
  { key: "decisionCriteria", label: "Decision Criteria", short: "D", desc: "Formal or informal criteria documented." },
  { key: "decisionProcess",  label: "Decision Process",  short: "D", desc: "Steps, timeline, and stakeholders documented." },
  { key: "paperProcess",     label: "Paper Process",     short: "P", desc: "Procurement / legal / signature path mapped." },
  { key: "identifyPain",     label: "Identify Pain",     short: "I", desc: "Specific named pain with business consequence." },
  { key: "champion",         label: "Champion",          short: "C", desc: "Active internal advocate, capable of selling internally." },
  { key: "competition",      label: "Competition",       short: "C", desc: "Competitors identified, position assessed." },
  { key: "risk",             label: "Risk",              short: "R", desc: "Deal risks identified, with mitigation owned and planned." },
];

const STAGES = [
  "Prospecting",
  "Qualification",
  "Discovery completed",
  "Demo / Evaluation",
  "Proposal sent",
  "Commercial negotiation",
  "Legal / Security review",
  "Verbal commitment",
  "Closed Won",
  "Closed Lost",
];

const FORECAST_CATEGORIES = ["Pipeline", "Best case", "Commit", "Closed"];

// Static option lists shared by the New / Edit dialogs across accounts
// and leads. Intentionally hard-coded (not in Admin settings) — they
// rarely change and live alongside STAGES / FORECAST_CATEGORIES as
// schema constants. Existing data values not on these lists still
// display correctly on read-only screens — only the dropdowns are
// constrained.
const INDUSTRIES = [
  "Banking",
  "Construction",
  "Consumer Packaged Goods",
  "Education",
  "Energy & Utilities",
  "Financial Services",
  "FinTech",
  "Government & Public Sector",
  "Healthcare",
  "HealthTech",
  "Industrial Automation",
  "Insurance",
  "Logistics & Supply Chain",
  "Manufacturing",
  "Media & Entertainment",
  "Non-profit",
  "Pharmaceutical",
  "Professional Services",
  "Real Estate",
  "Retail & E-commerce",
  "SaaS",
  "Software",
  "Telecommunications",
  "Transportation",
  "Travel & Hospitality",
  "Other",
];

// Standard B2B headcount bands. Matches the "201-1000" style already
// present in the seed fixture so demo accounts/leads keep rendering.
const HEADCOUNT_BANDS = [
  "1-10", "11-50", "51-200", "201-1000", "1001-5000", "5000+",
];

// Lead source channels. "Third party" covers purchased lists, data
// providers (ZoomInfo, Apollo, Clay, etc.), and other external sources
// where the contact did not come to us directly.
const LEAD_SOURCES = [
  "Inbound",
  "Outbound",
  "LinkedIn",
  "Referral",
  "Event",
  "Webinar",
  "Ads",
  "Third party",
  "Other",
];

// Sales regions. NA covers North America (US + Canada); LATAM is split
// out because the deal motion and currency mix typically differs from NA.
// "Global" is the catch-all for accounts that don't map to one geo —
// usually multinationals where the procurement happens HQ-side.
const REGIONS = ["NA", "EMEA", "APAC", "LATAM", "Global"];

// Annual-revenue bands. Anchored so the boundaries fall on round
// reporting points that match commonly-used B2B segmentations (sub-$10M
// is SME territory; $100M-$1B is upper mid-market / lower enterprise).
const REVENUE_BANDS = [
  "<$1M",
  "$1M-$10M",
  "$10M-$50M",
  "$50M-$100M",
  "$100M-$250M",
  "$250M-$500M",
  "$500M-$1B",
  "$1B+",
];

// Funding stage ladder. Includes both VC-track (pre-seed through Series E+)
// and outside-VC realities (Bootstrapped, Public, PE-backed) plus a
// non-profit / public-sector escape hatch for orgs that don't fit either.
const FUNDING_STAGES = [
  "Bootstrapped",
  "Pre-seed",
  "Seed",
  "Series A",
  "Series B",
  "Series C",
  "Series D",
  "Series E+",
  "Public",
  "PE-backed",
  "Non-profit / Public sector",
];

// Org-function dropdown for contact / lead "Department". Canonical B2B
// taxonomy; legacy free-text values not on this list are preserved as
// "(legacy)" options on the edit dialogs.
const DEPARTMENTS = [
  "Engineering",
  "Product",
  "Design",
  "Data",
  "IT",
  "Security",
  "Risk & Compliance",
  "Sales",
  "Marketing",
  "Customer Success",
  "RevOps",
  "Finance",
  "Legal",
  "HR / People",
  "Operations",
  "Procurement",
  "Executive",
  "Other",
];

// The six MEDDPICCR personas a survey can target. Each maps 1:1 to a
// /meddpicc/<slug>.html process page, to a contact's `icp`, and to an
// external public survey form URL configured in Admin -> Customisation.
const SURVEY_PERSONAS = [
  { slug: "compliance-executive", label: "Compliance Executive" },
  { slug: "engineering-leader",   label: "Engineering Leader" },
  { slug: "program-manager",      label: "Program Manager" },
  { slug: "transformation-cto",   label: "Transformation CTO" },
  { slug: "champion",             label: "Champion (Dev Advocate)" },
  { slug: "influencer",           label: "Influencer (Catalyst)" },
];

// Map of api.js resource name -> TPCRM_DATA key (uppercased).
const RESOURCE_KEYS = {
  users: "USERS", accounts: "ACCOUNTS", contacts: "CONTACTS",
  leads: "LEADS", opportunities: "OPPORTUNITIES", sequences: "SEQUENCES",
  surveys: "SURVEYS", interviews: "INTERVIEWS", insights: "INSIGHTS",
  quotes: "QUOTES", campaigns: "CAMPAIGNS", activities: "ACTIVITIES",
  referrals: "REFERRALS", rewards: "REWARDS", enrollments: "ENROLLMENTS",
  tasks: "TASKS", opportunityContacts: "OPPORTUNITY_CONTACTS",
  opportunityRisks: "OPPORTUNITY_RISKS",
  partners: "PARTNERS",
  renewalEvents: "RENEWAL_EVENTS",
  usageSnapshots: "USAGE_SNAPSHOTS",
  churnQualitative: "CHURN_QUALITATIVE",
  pilots: "PILOTS",
  pilotGates: "PILOT_GATES",
};

// Partner tier ladder + recognised partner types — shared between the
// New partner dialog (Sales -> Partners) and the partner-detail tab on
// an account page. Mirrors the backend allow-list in partnersController.
const PARTNER_TIERS = ["Bronze", "Silver", "Gold", "Platinum"];
const PARTNER_TYPES = ["Reseller", "Referral", "SI", "Alliance", "Tech alliance", "MSP"];

// Churn reason taxonomy — shared between the Details-tab reason chip and
// the renewal-event reason. Mirrors the backend allow-list in
// churnQualitativeController.js (REASONS set). Tied to a colour tone so
// the same chip renders consistently in both the table and the drawer.
const CHURN_REASON_CATEGORIES = [
  { key: "Champion left",     tone: "red"   },
  { key: "Budget cut",        tone: "amber" },
  { key: "Lack of adoption",  tone: "amber" },
  { key: "Competitive loss",  tone: "red"   },
  { key: "Missing feature",   tone: "blue"  },
  { key: "Pricing",           tone: "amber" },
  { key: "Service quality",   tone: "red"   },
  { key: "Acquired / merged", tone: "slate" },
  { key: "Other",             tone: "slate" },
];

const CHURN_STATUSES = [
  { key: "active",  label: "Active",  tone: "green" },
  { key: "at_risk", label: "At risk", tone: "amber" },
  { key: "churned", label: "Churned", tone: "red"   },
];

// ── DataProvider ──────────────────────────────────────────────────────

function DataProvider({ children }) {
  const [state, setState] = useState({ status: "loading", error: null });
  // Bumped after a successful tpReload so that components consuming
  // window.TPCRM_DATA via the D proxy in ui.jsx see the new data.
  const [, setTick] = useState(0);
  const aborterRef = useRef(null);

  const fetchAll = useCallback(() => {
    // Cancel any in-flight load so a Retry click can't be beaten by a
    // late-resolving first attempt.
    if (aborterRef.current) aborterRef.current.abort();
    const aborter = new AbortController();
    aborterRef.current = aborter;

    setState({ status: "loading", error: null });

    Promise.all([
      tpAPI.users.list(),
      tpAPI.accounts.list(),
      tpAPI.contacts.list(),
      tpAPI.leads.list(),
      tpAPI.opportunities.list(),
      tpAPI.sequences.list(),
      tpAPI.surveys.list(),
      tpAPI.interviews.list(),
      tpAPI.insights.list(),
      tpAPI.quotes.list(),
      tpAPI.campaigns.list(),
      tpAPI.activities.list(),
      tpAPI.referrals.list(),
      tpAPI.rewards.list(),
      tpAPI.enrollments.list(),
      tpAPI.tasks.list(),
      tpAPI.opportunityContacts.list(),
      tpAPI.opportunityRisks.list(),
      tpAPI.partners.list(),
      tpAPI.settings.get(),
      tpAPI.renewalEvents.list(),
      tpAPI.usageSnapshots.list(),
      tpAPI.churnQualitative.list(),
      tpAPI.pilots.list(),
      tpAPI.pilotGates.list(),
    ]).then(function ([
      USERS, ACCOUNTS, CONTACTS, LEADS, OPPORTUNITIES,
      SEQUENCES, SURVEYS, INTERVIEWS, INSIGHTS,
      QUOTES, CAMPAIGNS, ACTIVITIES, REFERRALS, REWARDS, ENROLLMENTS, TASKS,
      OPPORTUNITY_CONTACTS, OPPORTUNITY_RISKS, PARTNERS, SETTINGS,
      RENEWAL_EVENTS, USAGE_SNAPSHOTS, CHURN_QUALITATIVE,
      PILOTS, PILOT_GATES,
    ]) {
      if (aborter.signal.aborted) return;
      window.TPCRM_DATA = {
        USERS, ACCOUNTS, CONTACTS, LEADS, OPPORTUNITIES,
        SEQUENCES, SURVEYS, INTERVIEWS, INSIGHTS,
        QUOTES, CAMPAIGNS, ACTIVITIES, REFERRALS, REWARDS, ENROLLMENTS, TASKS,
        OPPORTUNITY_CONTACTS, OPPORTUNITY_RISKS, PARTNERS, SETTINGS,
        RENEWAL_EVENTS, USAGE_SNAPSHOTS, CHURN_QUALITATIVE,
        PILOTS, PILOT_GATES,
        MEDDPICCR_KEYS, STAGES, FORECAST_CATEGORIES, SURVEY_PERSONAS,
        INDUSTRIES, HEADCOUNT_BANDS, LEAD_SOURCES,
        REGIONS, REVENUE_BANDS, FUNDING_STAGES, DEPARTMENTS,
        PARTNER_TIERS, PARTNER_TYPES,
        CHURN_REASON_CATEGORIES, CHURN_STATUSES,
      };
      setState({ status: "ready", error: null });
    }).catch(function (err) {
      if (aborter.signal.aborted) return;
      console.error("crmCustomer data load failed:", err);
      setState({ status: "error", error: err.message });
    });
  }, []);

  // Re-fetch a single resource and bump tick so consumers re-render.
  const reloadResource = useCallback(async function (resourceName) {
    const dataKey = RESOURCE_KEYS[resourceName];
    if (!dataKey) return;
    try {
      const rows = await tpAPI[resourceName].list();
      if (window.TPCRM_DATA) {
        window.TPCRM_DATA[dataKey] = rows;
        setTick(function (t) { return t + 1; });
      }
    } catch (err) {
      console.error("crmCustomer reload(" + resourceName + ") failed:", err);
    }
  }, []);

  useEffect(function () {
    window.tpReload = reloadResource;
    return function () { if (window.tpReload === reloadResource) delete window.tpReload; };
  }, [reloadResource]);

  useEffect(function () { fetchAll(); }, [fetchAll]);

  if (state.status === "loading") {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100vh", gap: 16,
        fontFamily: "var(--sans)", color: "var(--text-muted)",
      }}>
        <img src="logo.png" alt="crmCustomer" style={{ height: 56, width: "auto" }} />
        <div style={{ fontSize: 14 }}>Loading crmCustomer…</div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100vh", gap: 12,
        fontFamily: "var(--sans)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--red)" }}>
          Failed to load data
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 360, textAlign: "center" }}>
          {state.error}
          <br /><br />
          Check that the backend is running and that{" "}
          <code>window.TPCRM_API_BASE</code> in <code>index.html</code>{" "}
          points to the correct URL.
        </div>
        <button
          onClick={fetchAll}
          style={{
            marginTop: 8, padding: "8px 20px", borderRadius: 6, border: "none",
            background: "var(--accent)", color: "#1c1917",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}>
          Retry
        </button>
      </div>
    );
  }

  return children;
}

Object.assign(window, { DataProvider });
